// Per-stage rollup for the producer Analytics view + PDF exports.
//
// Today the analytics page (apps/web/src/pages/Analytics.tsx) buckets
// metrics purely on string equality of `assignment.stage_name` ↔
// `presence_log.festival_area.name`. That misses non-artist
// assignments whose `stage_name` was synthesised as "Custom - <free
// text>" and never matched a real stage. After the schema redesign
// every assignment carries `stage_id`; this helper buckets by that
// FK first and falls back to the label only for legacy rows.

export type StageMin = {
  id: string;
  label: string;
  display_order: number;
};

export type AssignmentCategory = "detected" | "not_tracked" | "elsewhere" | "data_inconsistent";

export type AssignmentMin = {
  stage_id: string | null;
  stage_name: string | null;
  /** Minutes between filming_start and filming_end, midnight-aware. */
  durationMinutes?: number;
  /** True if a presence_log overlapped this assignment's shift window. */
  detected?: boolean;
  /**
   * 4-way diagnosis from analyzeAssignment(). When set, drives the
   * categorized counts on StageRollup. `detected`/`data_inconsistent`
   * are the GPS-confirmed buckets; `not_tracked` excludes the
   * assignment from the coverage denominator (we lack data, can't fairly
   * count it as a miss); `elsewhere` is a real miss (crew was on a
   * different stage during shift).
   */
  category?: AssignmentCategory;
  /** Clipped overlap minutes with shift window, ≤ durationMinutes. */
  coveredMinutes?: number;
};

export type PresenceMin = {
  stage_id: string | null;
  stage_name: string | null;
  durationMinutes: number;
};

export type StageRollup = {
  stageId: string | null;
  label: string;
  assignmentCount: number;
  /** Subset of assignmentCount: assignments where GPS detected presence. */
  detectedAssignmentCount: number;
  /**
   * Counts per diagnosis category from analyzeAssignment(). Sum equals
   * assignmentCount when every assignment was analyzed.
   */
  detectedCount: number;
  notTrackedCount: number;
  elsewhereCount: number;
  dataInconsistentCount: number;
  /** Sum of assignment durations — what crew was scheduled to be at the stage. */
  expectedTotalMinutes: number;
  /**
   * expectedTotalMinutes minus the duration of `not_tracked` assignments.
   * This is the denominator for coverage % — we don't punish crew for
   * shifts where our infrastructure failed to collect any GPS data.
   */
  trackableExpectedMinutes: number;
  /**
   * Sum of per-assignment clipped overlap, capped at each assignment's
   * expected duration. Bounded ≤ trackableExpectedMinutes by construction,
   * so coveredAssignmentMinutes / trackableExpectedMinutes is always ≤ 1.
   */
  coveredAssignmentMinutes: number;
  /** Sum of presence_log durations — what GPS actually detected. */
  presenceTotalMinutes: number;
  presenceVisits: number;
};

const UNASSIGNED_KEY = "__unassigned__";

export function aggregateByStage(input: {
  stages: StageMin[];
  assignments: AssignmentMin[];
  presenceLogs: PresenceMin[];
}): StageRollup[] {
  const { stages, assignments, presenceLogs } = input;

  const rollups = new Map<string, StageRollup>();
  const labelIndex = new Map<string, string>(); // label → stageId for legacy match

  const emptyRollup = (stageId: string | null, label: string): StageRollup => ({
    stageId,
    label,
    assignmentCount: 0,
    detectedAssignmentCount: 0,
    detectedCount: 0,
    notTrackedCount: 0,
    elsewhereCount: 0,
    dataInconsistentCount: 0,
    expectedTotalMinutes: 0,
    trackableExpectedMinutes: 0,
    coveredAssignmentMinutes: 0,
    presenceTotalMinutes: 0,
    presenceVisits: 0,
  });

  for (const stage of stages) {
    rollups.set(stage.id, emptyRollup(stage.id, stage.label));
    labelIndex.set(stage.label, stage.id);
  }

  const ensureUnassigned = (): StageRollup => {
    let r = rollups.get(UNASSIGNED_KEY);
    if (!r) {
      r = emptyRollup(null, "Unassigned");
      rollups.set(UNASSIGNED_KEY, r);
    }
    return r;
  };

  const resolve = (stageId: string | null, stageName: string | null): StageRollup => {
    if (stageId && rollups.has(stageId)) return rollups.get(stageId)!;
    if (stageName) {
      const matched = labelIndex.get(stageName);
      if (matched) return rollups.get(matched)!;
    }
    return ensureUnassigned();
  };

  for (const a of assignments) {
    const r = resolve(a.stage_id, a.stage_name);
    r.assignmentCount += 1;
    const dur = a.durationMinutes ?? 0;
    r.expectedTotalMinutes += dur;
    if (a.detected === true) r.detectedAssignmentCount += 1;

    // Category-driven rollups. We only contribute to trackableExpected
    // and coveredAssignmentMinutes for categories where GPS data
    // actually existed — not_tracked assignments are excluded from
    // the coverage ratio so a stuck-bundle bug doesn't drag the score.
    switch (a.category) {
      case "detected":
        r.detectedCount += 1;
        r.trackableExpectedMinutes += dur;
        r.coveredAssignmentMinutes += a.coveredMinutes ?? 0;
        break;
      case "data_inconsistent":
        r.dataInconsistentCount += 1;
        r.trackableExpectedMinutes += dur;
        r.coveredAssignmentMinutes += a.coveredMinutes ?? 0;
        break;
      case "elsewhere":
        r.elsewhereCount += 1;
        r.trackableExpectedMinutes += dur;
        // coveredMinutes is 0 for elsewhere by construction
        break;
      case "not_tracked":
        r.notTrackedCount += 1;
        break;
      default:
        // No category provided — legacy caller. Don't contribute to
        // any of the new fields so old callers still see zero for them.
        break;
    }
  }

  for (const p of presenceLogs) {
    const r = resolve(p.stage_id, p.stage_name);
    r.presenceTotalMinutes += p.durationMinutes;
    r.presenceVisits += 1;
  }

  // Stages first in display_order, then Unassigned (if present) at the
  // end. Iteration order of `rollups` already follows insertion order
  // for known stages; we need to sort to honor display_order in case
  // the caller passed `stages` unsorted.
  const knownOrdered = stages
    .slice()
    .sort((a, b) => a.display_order - b.display_order)
    .map((s) => rollups.get(s.id)!);

  const unassigned = rollups.get(UNASSIGNED_KEY);
  return unassigned ? [...knownOrdered, unassigned] : knownOrdered;
}
